import { useState, useEffect, useMemo } from 'react';

import styled from 'styled-components';
import moment from 'moment';
import Lottie from 'lottie-react';

import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';
import { useLanguage } from '../../../Context/LanguageContext';

import RevenueChart from './RevenueChart';
import RevenueHistory from './RevenueHistory';

import LoadingAnimation from '../../../Animation/NewLoading.json';

export default function RevenueBox(props) {
  const {
    today,
    calcDateText,

    isMonthMode,
    startDate,
    endDate,
    periodYearSales,

    totalMaxPrice,
    revenue,
    prevRevenue,

    selectedPartnerGym,
    isOpenPartnerGymSales,

    isRevenueLoading,
    setIsRevenueLoading,

    salesData,
    isSalesLoading,
    setIsSalesLoading
  } = props;

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

  const [thisYear, setThisYear] = useState([]);
  const [thisYearMaxPrice, setThisYearMaxPrice] = useState(0);
  const [viewMode, setViewMode] = useState('year');

  // 구간 모드: 실제 개월(startDate~endDate) 막대 + 구간 포함 연도별 비교 라인 //
  const periodChart = useMemo(() => {
    if (isMonthMode) return null;

    const startYear = startDate.year();
    const endYear = endDate.year();

    // 연도별 1~12월 매출 맵 (라인 비교용) //
    const yearMap = new Map();

    for (let year = startYear; year <= endYear; year++) {
      yearMap.set(year, Array.from({ length: 12 }, () => 0));
    }

    for (const sale of periodYearSales) {
      const date = moment(sale.approvalDate);
      const year = date.year();

      if (!yearMap.has(year)) continue;

      yearMap.get(year)[date.month()] += sale.totalPrice;
    }

    const lineYears = Array.from(yearMap.keys()).sort((a, b) => a - b);

    // 구간 실제 개월 슬롯 생성 (예: 25.3, 25.4 ... 26.2) //
    const data = [];
    const cursor = startDate.clone().startOf('month');
    const last = endDate.clone().startOf('month');

    // 가로축 개월 수는 데이터 유무와 무관하게 startDate~endDate 기준으로 고정 //
    const monthCount = last.diff(startDate.clone().startOf('month'), 'months') + 1;

    // 강조 막대는 실제 시각(지금) 기준 현재 월 //
    const now = moment();
    const nowYear = now.year();
    const nowMonth = now.month();

    let periodTotal = 0;
    let selectedIndex = -1;

    while (cursor.isSameOrBefore(last, 'month')) {
      const slotYear = cursor.year();
      const slotMonth = cursor.month();
      const barValue = yearMap.get(slotYear)?.[slotMonth] || 0;

      const row = {
        name: cursor.format('YY.M'),
        year: slotYear,
        month: slotMonth,
        bar: barValue
      };

      // 각 연도 라인은 해당 슬롯의 '달' 기준 매출로 정렬 //
      for (const [year, months] of yearMap) {
        row['y' + year] = months[slotMonth];
      }

      if (slotYear === nowYear && slotMonth === nowMonth) selectedIndex = data.length;

      data.push(row);
      periodTotal += barValue;

      cursor.add(1, 'month');
    }

    // 최댓값 (막대 + 모든 연도 라인) //
    let maxPrice = 0;

    for (const row of data) {
      if (row.bar > maxPrice) maxPrice = row.bar;

      for (const year of lineYears) {
        if (row['y' + year] > maxPrice) maxPrice = row['y' + year];
      }
    }

    return { data, lineYears, maxPrice, periodTotal, selectedIndex, monthCount };
  }, [isMonthMode, startDate, endDate, periodYearSales, today]);

  // 월별 매출 데이터 집계 //
  useEffect(() => {
    const months = Array.from({ length: 12 }, () => ({
      totalPrice: 0,
      cashPrice: 0,
      cardPrice: 0,
      discountPrice: 0,
      notReceived: 0,
      prevTotalPrice: 0,
      sales: [],
      selectedPaymentOptions: []
    }));

    let maxPrice = 0;

    for (const sale of revenue) {
      const monthIndex = parseInt(sale.approvalDate.split('T')[0].split('-')[1], 10) - 1;

      if (monthIndex < 0 || monthIndex > 11) continue;

      const month = months[monthIndex];

      month.totalPrice += sale.totalPrice;
      month.cashPrice += sale.cashPrice;
      month.cardPrice += sale.cardPrice;
      month.notReceived += sale.notReceivedPrice;
      month.sales.push(sale);

      // 결제 옵션 집계 //
      for (const option of sale.selectedPaymentOptions) {
        if (option.paymentOptionId === 0) continue;

        const existing = month.selectedPaymentOptions.find(
          o => o.paymentOptionId === option.paymentOptionId
        );

        if (existing) {
          existing.price += option.price;
        }
        else {
          month.selectedPaymentOptions.push({
            paymentOptionId: option.paymentOptionId,
            paymentOptionName: option.paymentOptionName,
            price: option.price
          });
        }
      }

      month.selectedPaymentOptions.sort((a, b) => a.paymentOptionId - b.paymentOptionId);

      if (month.totalPrice > maxPrice) maxPrice = month.totalPrice;
    }

    // 전년도 매출 집계 //
    for (const sale of prevRevenue) {
      const monthIndex = parseInt(sale.approvalDate.split('T')[0].split('-')[1], 10) - 1;

      if (monthIndex >= 0 && monthIndex <= 11) {
        months[monthIndex].prevTotalPrice += sale.totalPrice;
      }
    }

    setThisYear(months);
    setThisYearMaxPrice(maxPrice);

    setTimeout(() => setIsRevenueLoading(false), 1000);
  }, [revenue, prevRevenue, today]);

  // 매출 증감 계산 (viewMode에 따라 YOY / MOM 분기) //
  function calcChange() {
    if (thisYear.length === 0) return { diff: 0, percent: null, direction: 'none' };

    const currentIndex = today.month();
    const current = thisYear[currentIndex]?.totalPrice || 0;
    let prev = 0;

    if (viewMode === 'year') {
      prev = thisYear[currentIndex]?.prevTotalPrice || 0;
    }
    else {
      const prevIndex = currentIndex - 1;

      if (prevIndex >= 0) prev = thisYear[prevIndex]?.totalPrice || 0;
    }

    if (prev === 0) return { diff: current, percent: null, direction: 'none' };

    const diff = current - prev;
    const percent = Math.round((diff / prev) * 100);
    const direction = diff > 0 ? 'up' : diff < 0 ? 'down' : 'same';

    return { diff, percent, direction };
  }

  // 증감(+/-) 텍스트 결정 //
  function renderYoyText() {
    const { diff, percent, direction } = calcChange();

    if (direction === 'none' && diff === 0) {
      return (
        <YoyTextWrapper>
          <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.text} cursor='default'>
            {t('statistics.chart.noStandard')}
          </Text>
        </YoyTextWrapper>
      );
    }

    const sign = diff >= 0 ? '+ ' : '- ';
    const diffText = sign + Math.abs(diff).toLocaleString('ko-KR') + t('statistics.won');
    const percentText = percent !== null ? ' (' + (diff >= 0 ? '+' : '') + percent + '%)' : '';
    const label = diff > 0 ? t('statistics.revenue.higher') : diff < 0 ? t('statistics.revenue.lower') : t('statistics.revenue.same');
    const accentColor = diff >= 0 ? theme.brand500 : theme.brand500;
    const comparedLabel = viewMode === 'year' ? t('statistics.revenue.comparedToLastYear') : t('statistics.revenue.comparedToLastMonth');

    return (
      <YoyTextWrapper>
        <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.text} cursor='default'>
          {comparedLabel}
        </Text>
        <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={accentColor} cursor='default'>
          {diffText}{percentText}
        </Text>
        <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.text} cursor='default'>
          {label}
        </Text>
      </YoyTextWrapper>
    );
  }

  // 차트 범례 렌더 (월 모드: 올해/작년 · 구간 모드: 연도별 다색) //
  function renderLegend() {
    if (isMonthMode) {
      return (
        <LegendWrapper>
          <LegendItem>
            <LegendBar backgroundColor={theme.brand500} />
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text} cursor='default'>
              {today.clone().format('YYYY')}
            </Text>
          </LegendItem>
          <LegendItem>
            <LegendDashedBar $color={theme.subText} />
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.subText} cursor='default'>
              {t('statistics.revenue.lastYearSales')}
            </Text>
          </LegendItem>
        </LegendWrapper>
      );
    }

    if (periodChart === null) return null;

    return (
      <LegendWrapper>
        {
          periodChart.lineYears.map((year, index) => (
            <LegendItem key={'period-legend-' + year}>
              <LegendDashedBar $color={theme.chartYearPalette[index % theme.chartYearPalette.length]} />
              <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text} cursor='default'>
                {year}
              </Text>
            </LegendItem>
          ))
        }
      </LegendWrapper>
    );
  }

  // 헤더 타이틀/총액 결정 (월 모드: 해당 월 · 구간 모드: 기간 + 최근 연도 합계) //
  function decideHeaderTitle() {
    return isMonthMode ? today.clone().format(t('statistics.dateFormatMonthShort')) : calcDateText();
  }

  function decideHeaderTotal() {
    if (isMonthMode) return thisYear[today.month()]?.totalPrice || 0;

    return periodChart?.periodTotal || 0;
  }

  // 강조(호버색) 막대 인덱스 결정: 실제 시각 기준 현재 월 //
  function decideSelectedMonth() {
    const now = moment();

    if (isMonthMode) return today.year() === now.year() ? now.month() : -1;

    return periodChart?.selectedIndex ?? -1;
  }

  return (
    <TopLevelWrapper $isDark={isDark}>
      {
        isRevenueLoading ?
          <AnimationBox>
            <LottieWrapper>
              <Lottie animationData={LoadingAnimation} loop={true} />
            </LottieWrapper>
          </AnimationBox>
          :
        isOpenPartnerGymSales ?
          <ContentWrapper>
            <HeaderWrapper>
              <Text fontFamily='NotoSansKR-Bold' fontSize={20} color={theme.text} cursor='default'>
                {decideHeaderTitle()}
              </Text>
              <PriceWrapper>
                <Text fontFamily='NotoSansKR-Bold' fontSize={20} color={theme.text} cursor='default'>
                  {decideHeaderTotal().toLocaleString('ko-KR')}
                </Text>
                <Text fontFamily='NotoSansKR-Bold' fontSize={14} color={theme.text} cursor='default'>
                  {t('statistics.won')}
                </Text>
              </PriceWrapper>
            </HeaderWrapper>

            <ChartSection>
              <ChartHeaderWrapper>
                <ViewModeToggleWrapper>
                  <ViewModeButton
                    $isActive={viewMode === 'month'}
                    $isDark={isDark}
                    onClick={() => setViewMode('month')}>
                    <Text fontFamily='NotoSansKR-Medium' fontSize={15} color={viewMode === 'month' ? theme.subBackground : theme.text} cursor='pointer'>
                      {t('statistics.revenue.oneMonth')}
                    </Text>
                  </ViewModeButton>
                  <ViewModeButton
                    $isActive={viewMode === 'year'}
                    $isDark={isDark}
                    onClick={() => setViewMode('year')}>
                    <Text fontFamily='NotoSansKR-Medium' fontSize={15} color={viewMode === 'year' ? theme.subBackground : theme.text} cursor='pointer'>
                      {t('statistics.revenue.oneYear')}
                    </Text>
                  </ViewModeButton>
                </ViewModeToggleWrapper>

                {renderYoyText()}
              </ChartHeaderWrapper>

              <RevenueChart
                isMonthMode={isMonthMode}
                periodData={periodChart}
                thisYear={thisYear}
                thisYearMaxPrice={thisYearMaxPrice}
                selectedMonth={decideSelectedMonth()}
              />

              {renderLegend()}
            </ChartSection>

            <RevenueHistory
              calcDateText={calcDateText}
              data={salesData}
              selectedPartnerGym={selectedPartnerGym}
              isOpenPartnerGymSales={isOpenPartnerGymSales}
              isSalesLoading={isSalesLoading}
              setIsSalesLoading={setIsSalesLoading}
            />
          </ContentWrapper>
          :
          <NotOpenTextWrapper>
            <Text fontFamily='NotoSansKR-Bold' fontSize={15} color={theme.subText} cursor='default'>
              {t('statistics.partnerGymPrivate')}
            </Text>
            <Text fontFamily='NotoSansKR-Regular' fontSize={15} color={theme.subText} cursor='default'>
              {t('statistics.partnerGymPrivateDesc')}
            </Text>
          </NotOpenTextWrapper>
      }
    </TopLevelWrapper>
  );
}

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  padding: 20px 40px;

  gap: 10px;

  width: calc(100% - 80px);

  margin: 39.5px 0;

  border-radius: 20px;
  background-color: ${props => props.theme.subBackground};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;

  gap: 10px;
`;

const AnimationBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 400px;
`;

const LottieWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 160px;
  height: 160px;
`;

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  width: 100%;
`;

const PriceWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 5px;
`;

const ChartSection = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;

  gap: 10px;
`;

const ChartHeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 100%;

  gap: 20px;
`;

const YoyTextWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 10px;

  width: 100%;
`;

const LegendWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-end;
  align-items: center;

  width: 100%;

  padding-bottom: 20px;

  gap: 10px;
`;

const LegendItem = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 10px;
`;

const LegendBar = styled.div`
  width: 40px;
  height: 7px;

  border-radius: 2px;
  background-color: ${props => props.backgroundColor};
`;

const LegendDashedBar = styled.div`
  width: 40px;
  height: 0px;

  border-top: 2px dashed ${props => props.$color};
`;

const ViewModeToggleWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 207px;
  height: 41px;

  gap: 5px;

  border-radius: 8px;
  background-color: ${props => props.theme.buttonFill};

  flex-shrink: 0;
`;

const ViewModeButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 96px;
  height: 29px;

  border-radius: 6px;
  background-color: ${props => props.$isActive ? props.theme.brand500 : 'transparent'};
  box-shadow: ${props => props.$isActive ? '0px 9px 7.1px 0px rgba(0, 0, 0, 0.05)' : 'none'};

  cursor: pointer;

  transition: all 0.3s ease-in-out;
`;

const NotOpenTextWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 300px;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;

  &:hover {
    cursor: ${props => props.cursor};
  }
`;